<template>
<section class="feedback-form">
  <h2 class="feedback-form-title">{{ title }}</h2>
  <h4 class="feedback-form-subtitle">{{ content }}</h4>

  <div v-if="success" class="alert alert-success">
    <h4 class="alert-heading">Спасибо, ваше сообщение было отправлено!</h4>
    <p>Мы ответим вам как можно скорее.</p>
  </div>

  <div class="form-row">
    <div v-if="settings.name_enabled" class="form-col" :class="firstRowColumnsClass">
      <div class="form-group" :class="{ 'has-error': errors.name }">
        <label for="feedback-form-name" class="form-control-label">Ваше имя</label>
        <input type="text" class="form-control" id="feedback-form-name" v-model="form.name" @keyup.enter="sendFeedbackRequest">
        <span class="form-control-feedback" v-if="errors.name">{{ errors.name }}</span>
      </div>
    </div>
    <div class="form-col" :class="firstRowColumnsClass">
      <div v-if="settings.contact_type === 'email'" class="form-group" :class="{ 'has-error': errors.email }">
        <label for="feedback-form-email" class="form-control-label">Email</label>
        <input type="email" class="form-control" id="feedback-form-email" v-model="form.email" @keyup.enter="sendFeedbackRequest">
        <span class="form-control-feedback" v-if="errors.email">{{ errors.email }}</span>
      </div>
      <div v-else class="form-group" :class="{ 'has-error': errors.phone }">
        <label for="feedback-form-phone" class="form-control-label">Телефон</label>
        <phone-input id="feedback-form-phone" v-model="form.phone" @input:country="form.phone_country = $event" />
        <span class="form-control-feedback" v-if="errors.phone">{{ errors.phone }}</span>
      </div>
    </div>
  </div>
  <div class="form-row" v-if="settings.message_enabled">
    <div class="form-col form-col-full">
      <div class="form-group" :class="{ 'has-error': errors.comment }">
        <label for="feedback-form-comment" class="form-control-label">
          {{ label }}
        </label>
        <textarea class="form-control"
                  rows="5"
                  id="feedback-form-comment"
                  v-model="form.comment"
                  @keyup.enter.ctrl="sendFeedbackRequest"
        ></textarea>
        <span class="form-control-feedback" v-if="errors.comment">{{ errors.comment }}</span>
      </div>
    </div>
  </div>
  <div class="form-row">
    <div class="form-col form-col-full">
      <button type="button"
              class="btn btn-block btn-primary btn-sm btn-shadow-md btn-rounded"
              :disabled="submissionDisabled"
              @click="sendFeedbackRequest"
      >
        <i class="fal fa-spinner fa-spin" v-if="formIsBusy"></i>
        Написать
      </button>
    </div>
  </div>
</section>
</template>

<script>
import axios from '@/zenky'
import { mapGetters, mapActions } from 'vuex'
import { validationErrors } from '@/helpers/validation'
import zenkyEvents from '@/events'
import PhoneInput from '@/components/inputs/PhoneInput'

export default {
  props: {
    title: {
      type: String,
      required: false,
      default: 'У вас есть вопросы или предложения?',
    },
    content: {
      type: String,
      required: false,
      default: 'Оставьте сообщение и мы обязательно свяжемся с вами',
    },
    label: {
      type: String,
      required: false,
      default: 'Ваш вопрос или предложение',
    },
  },

  components: {
    PhoneInput
  },

  computed: {
    ...mapGetters({
      reCaptchaIsReady: 'recaptcha/isReady',
      settings: 'settings/feedbackForm',
    }),

    submissionDisabled() {
      if (this.formIsBusy) {
        return true
      }

      return this.$store.state.recaptcha.actions.includes('feedback_request') && !this.reCaptchaIsReady
    },

    firstRowColumnsClass() {
      if (this.settings.name_enabled) {
        return ['w-full mb-8', 'sm:mb-0 sm:w-1/2']
      }

      return ['w-full']
    }
  },

  methods: {
    ...mapActions({
      getReCaptchaToken: 'recaptcha/getToken',
    }),

    sendFeedbackRequest() {
      if (this.formIsBusy) {
        return
      }

      this.success = false
      this.resetErrors()

      if (this.validateForm() !== true) {
        return
      }

      this.getReCaptchaToken('feedback_request').then(token => {
        const form = {
          name: this.form.name,
          comment: this.form.comment,
        }

        if (this.settings.contact_type === 'email') {
          form.email = this.form.email
        } else {
          form.phone = this.form.phone
          form.phone_country = this.form.phone_country
        }

        if (token) {
          form.recaptcha = { token }
        }

        this.formIsBusy = true

        axios.post('feedback', form)
          .then(({ data }) => {
            this.formIsBusy = false
            this.success = true

            this.$root.sharedEventBus.$emit(zenkyEvents.FEEDBACK_SUBMITTED, data)
          })
          .catch(err => {
            this.formIsBusy = false
            this.processErrorResponse(
              validationErrors(err),
              err
            )
          })
      })
    },

    resetErrors() {
      this.errors.name = ''
      this.errors.email = ''
      this.errors.phone = ''
      this.errors.comment = ''
    },

    validateForm() {
      if (this.settings.name_enabled && !this.form.name.trim()) {
        return this.errors.name = 'Необходимо указать имя.'
      } else if (this.settings.contact_type === 'email' && !this.form.email.trim()) {
        return this.errors.email = 'Необходимо указать email.'
      } else if (this.settings.contact_type === 'phone' && !this.form.phone.trim()) {
        return this.errors.email = 'Необходимо указать телефон.'
      } else if (this.settings.message_enabled && !this.form.comment.trim()) {
        return this.errors.comment = 'Необходимо указать сообщение.'
      }

      return true
    },

    processErrorResponse(errors, err) {
      if (errors.name) {
        return this.errors.name = 'Необходимо указать имя.'
      } else if (errors.email) {
        return this.errors.email = 'Необходимо указать email.'
      } else if (errors.phone) {
        return this.errors.phone = 'Необходимо указать телефон.'
      } else if (errors.comment) {
        return this.errors.comment = 'Необходимо указать сообщение.'
      }
    },
  },

  data() {
    return {
      formIsBusy: false,
      success: false,

      form: {
        name: '',
        email: '',
        phone: '',
        phone_country: 'RU',
        comment: '',
      },

      errors: {
        name: '',
        email: '',
        phone: '',
        comment: '',
      }
    }
  }
}
</script>

<style lang="less">
.feedback-form {
  @apply text-center;

  .feedback-form-title {
    @apply text-3xl font-bold mb-4;
  }

  .feedback-form-subtitle {
    @apply text-lg font-normal mb-8;
  }
}

.alert {
  @apply p-4 rounded bg-grey-lighter mb-4;

  .alert-heading {
    @apply text-xl font-bold mb-2;
  }

  p {
    @apply mb-2;

    &:last-child {
      @apply mb-0;
    }
  }

  a {
    @apply font-bold;
  }

  &.alert-primary {
    @apply bg-primary-lighter text-primary-darker;

    a {
      @apply text-primary-darkest;
    }
  }

  &.alert-secondary {
    @apply bg-secondary-lighter text-secondary-darker;

    a {
      @apply text-secondary-darkest;
    }
  }

  &.alert-success {
    @apply bg-success-lighter text-success-darker;

    a {
      @apply text-success-darkest;
    }
  }

  &.alert-info {
    @apply bg-info-lighter text-info-darker;

    a {
      @apply text-info-darkest;
    }
  }

  &.alert-warning {
    @apply bg-warning-lighter text-warning-darker;

    a {
      @apply text-warning-darkest;
    }
  }

  &.alert-danger {
    @apply bg-danger-lighter text-danger-darker;

    a {
      @apply text-danger-darkest;
    }
  }

  &.alert-light {
    @apply bg-light-lighter text-light-darker;

    a {
      @apply text-light-darkest;
    }
  }

  &.alert-dark {
    @apply bg-dark-lighter text-dark-darker;

    a {
      @apply text-dark-darkest;
    }
  }
}
</style>
